import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Button,
  Spinner,
  Alert,
  Progress,
} from "reactstrap";
import { getPaymentStatus } from "actions/quotationReview";

import ProjectLogo from "../../assets/img/icon-transparent.png";
import NotificationAlert from "react-notification-alert";
import { downloadPaymentReceipt } from "actions/quotationReview";

const PaymentStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uuid, setUuid] = useState(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const notificationAlertRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    id: "",
    amount: 0,
    date: "",
    paymentMethod: "",
    transactionId: "",
  });

  useEffect(() => {
    // Add history entry only once
    if (!window.history.state?.custom) {
      window.history.pushState({ custom: true }, "", window.location.href);
    }

    const handlePopState = () => {
      navigate("/", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    setUuid(id);
    const resourcePath = params.get("resourcePath");
    const status = params.get("status");

    verifyPaymentStatus(id, resourcePath, status);
  }, [location.search]);

  const downloadReceipt = async () => {
    setIsDownloading(true);

    try {
      // res is directly Blob
      const blob = await downloadPaymentReceipt(paymentDetails?.transactionId);

      if (!blob) {
        notify("danger", "Failed to download receipt.");
        return;
      }

      const fileName = `payment_receipt_${paymentDetails?.transactionId}.pdf`;

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = url;
      a.download = fileName;

      document.body.appendChild(a);

      a.click();

      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);

      notify("success", "Receipt downloaded successfully.");
    } catch (error) {
      console.error(error);

      notify("danger", error?.message || "Failed to download receipt.");
    }

    setIsDownloading(false);
  };

  const verifyPaymentStatus = async (id, resourcePath, status) => {
    setLoading(true);
    setIsSessionExpired(false);
    try {
      let bodyData = {
        resourcePath: resourcePath || "",
      };
      const res = await getPaymentStatus(bodyData);
      // Check for 500 error or session expiration
      if (res?.code === 500 || res?.status === "FAILED") {
        setPaymentStatus(false);
        setIsSessionExpired(true);
        setPaymentDetails({
          id: "",
          amount: 0,
          date: "",
          paymentMethod: "",
          transactionId: res?.data?.merchantTransactionID || "",
          message: res?.message || "Session expired. Please try again.",
        });
        setLoading(false);
      } else if (
        res?.code === 200 &&
        res?.message === "Payment processed successfully."
      ) {
        setPaymentStatus(true);
        setIsSessionExpired(false);
        setPaymentDetails({
          id: res?.data?.paymentTransactionID || "",
          amount: res?.data?.transactionAmount || 0,
          date: res?.data?.paymentResponseTime || "",
          paymentMethod: res?.data?.paymentMethod || "",
          transactionId: res?.data?.merchantTransactionID || "",
          message: res?.message || "",
        });
        setLoading(false);
      } else {
        setPaymentStatus(false);
        setIsSessionExpired(false);
        setPaymentDetails({
          id: res?.data?.paymentTransactionID || "",
          amount: res?.data?.transactionAmount || 0,
          date: res?.data?.paymentResponseTime || "",
          paymentMethod: res?.data?.paymentMethod || "",
          transactionId: res?.data?.merchantTransactionID || "",
          message: res?.message || "Payment verification failed.",
        });
        setLoading(false);
      }
    } catch (err) {
      setPaymentStatus(false);
      // Check if error indicates session expiration
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while verifying payment status.";
      const isExpired =
        errorMessage.toLowerCase().includes("session") ||
        errorMessage.toLowerCase().includes("expired") ||
        err?.response?.status === 401 ||
        err?.response?.status === 403;

      setIsSessionExpired(isExpired);
      setPaymentDetails({
        id: "",
        amount: 0,
        date: "",
        paymentMethod: "",
        transactionId: "",
        message: isExpired
          ? "Your session has expired. Please try again."
          : errorMessage,
      });
      setLoading(false);
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const showPendingMessage = () => {
    notify(
      "danger",
      "Receipt download is currently unavailable. API integration for receipt generation is pending.",
    );
  };

  const handleDownloadReceipt = () => {
    // Create receipt content
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment Receipt</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #007bff;
          }
          .logo {
            max-width: 150px;
            margin-bottom: 15px;
          }
          .title {
            color: #28a745;
            font-size: 28px;
            margin: 10px 0;
          }
          .receipt-details {
            margin: 30px 0;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
          }
          .detail-row {
            margin: 10px 0;
            padding: 8px 0;
            border-bottom: 1px solid #dee2e6;
          }
          .detail-label {
            font-weight: bold;
            display: inline-block;
            width: 150px;
          }
          .detail-value {
            display: inline-block;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #dee2e6;
            color: #6c757d;
            font-size: 12px;
          }
          .status-success {
            color: #28a745;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${ProjectLogo}" alt="Logo" class="logo" style="max-width: 150px;" />
          <h1 class="title">Payment Receipt</h1>
          <p>Thank you for your purchase</p>
        </div>
        
        <div class="receipt-details">
          <div class="detail-row">
            <span class="detail-label">Transaction ID:</span>
            <span class="detail-value">${paymentDetails.transactionId}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Payment ID:</span>
            <span class="detail-value">${paymentDetails.id}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Amount Paid:</span>
            <span class="detail-value">$${paymentDetails.amount.toFixed(2)}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Payment Method:</span>
            <span class="detail-value">${paymentDetails.paymentMethod}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Transaction Date:</span>
            <span class="detail-value">${paymentDetails.date}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Status:</span>
            <span class="detail-value status-success">Successfully Paid</span>
          </div>
        </div>
        
        <div class="footer">
          <p>This is a computer-generated receipt and does not require a signature.</p>
          <p>For any queries, please contact our support team.</p>
        </div>
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([receiptHTML], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `receipt_${paymentDetails.transactionId}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleTryAgain = () => {
    navigate(`/review-quotation/${uuid}`);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="text-center shadow-lg border-0">
              <CardBody className="py-5">
                <Spinner
                  color="primary"
                  style={{ width: "3rem", height: "3rem" }}
                >
                  Loading...
                </Spinner>
                <CardTitle tag="h4" className="mt-4">
                  Verifying Payment
                </CardTitle>
                <CardText>
                  Please wait while we confirm your payment status...
                </CardText>
                <Progress
                  animated
                  color="primary"
                  value={100}
                  className="mt-3"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  const isSuccess = paymentStatus === true;
  const isExpired = isSessionExpired;
  const statusColor = isSuccess ? "success" : isExpired ? "warning" : "danger";
  const statusBgColor = isSuccess
    ? "#d4edda"
    : isExpired
      ? "#fff3cd"
      : "#f8d7da";
  const statusTitle = isSuccess
    ? "Payment Successful!"
    : isExpired
      ? "Session Expired"
      : "Payment Failed";
  const statusMessage = isSuccess
    ? "Your payment has been processed successfully. Thank you for your purchase"
    : isExpired
      ? "Your session has expired. Please try again to complete your payment."
      : "We couldn't process your payment. Please try again or use a different payment method.";

  // Icon components using simple SVG or emoji
  const SuccessIcon = () => (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#28a745"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );

  const ErrorIcon = () => (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#dc3545"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  const WarningIcon = () => (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#856404"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );

  const DownloadIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: "8px" }}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );

  const HomeIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: "8px" }}
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-5v-8H7v8H5a2 2 0 0 1-2-2z" />
    </svg>
  );

  const ShoppingIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: "8px" }}
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );

  const AlertIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: "8px" }}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );

  const RefreshIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: "8px" }}
    >
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
    </svg>
  );

  return (
    <>
      {isDownloading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Downloading… Please wait.
            </div>
          </div>
        </div>
      )}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            {/* Project Logo Header */}
            <div className="text-center mb-4">
              <img
                src={ProjectLogo}
                alt="Project Logo"
                style={{
                  maxWidth: "180px",
                  height: "auto",
                  marginBottom: "20px",
                }}
              />
            </div>

            {/* Status Card */}
            <Card className="shadow-lg border-0 mb-4">
              <CardBody className="text-center py-5">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: statusBgColor,
                  }}
                >
                  {isSuccess ? (
                    <SuccessIcon />
                  ) : isExpired ? (
                    <WarningIcon />
                  ) : (
                    <ErrorIcon />
                  )}
                </div>

                <CardTitle tag="h3" className={`text-${statusColor} mb-3`}>
                  {/* {statusTitle} */}
                  {paymentDetails.message}
                </CardTitle>

                {/* <CardText className="text-muted mb-4">
                  {paymentDetails.message}
                </CardText> */}

                {/* <Alert
                  color={
                    isSuccess ? "success" : isExpired ? "warning" : "danger"
                  }
                  className="text-start"
                >
                  <AlertIcon />
                  {statusMessage}
                </Alert> */}

                {/* Additional helpful message for expired session */}
                {isExpired && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <small className="text-muted">
                      <strong>Note:</strong> Payment sessions typically expire
                      after 15-30 minutes for security reasons. Please start a
                      new payment process.
                    </small>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Payment Details Card - Only show if payment was attempted */}
            {paymentDetails.transactionId && !isExpired && (
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <CardTitle tag="h5" className="mb-4 pb-2 border-bottom">
                    Payment Details
                  </CardTitle>

                  <div className="mb-3">
                    <small className="text-muted d-block">
                      Merchant Transaction ID
                    </small>
                    <strong>{paymentDetails.transactionId}</strong>
                  </div>

                  {paymentDetails.id && (
                    <div className="mb-3">
                      <small className="text-muted d-block">Payment ID</small>
                      <strong>{paymentDetails.id}</strong>
                    </div>
                  )}

                  {paymentDetails.amount > 0 && (
                    <div className="mb-3">
                      <small className="text-muted d-block">Amount Paid</small>
                      <strong className="fs-4 text-success">
                        ${paymentDetails.amount.toFixed(2)}
                      </strong>
                    </div>
                  )}

                  {paymentDetails.date && (
                    <div className="mb-3">
                      <small className="text-muted d-block">
                        Transaction Date
                      </small>
                      <strong>{paymentDetails.date}</strong>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              {isSuccess && (
                <Button
                  color="success"
                  onClick={downloadReceipt}
                  // onClick={showPendingMessage}
                  className="px-4"
                >
                  <DownloadIcon />
                  Download Receipt
                </Button>
              )}

              <Button
                color="primary"
                outline
                onClick={handleGoHome}
                className="px-4"
              >
                <HomeIcon />
                Go to Home
              </Button>

              {!isSuccess && !isExpired && (
                <Button
                  color="warning"
                  onClick={handleTryAgain}
                  className="px-4"
                >
                  <ShoppingIcon />
                  Try Again
                </Button>
              )}

              {isExpired && (
                <Button
                  color="warning"
                  onClick={() => window.location.reload()}
                  className="px-4"
                >
                  <RefreshIcon />
                  Refresh Page
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PaymentStatus;
